/**
 * LocaleSelector - 다국어 선택 드롭다운
 *
 * 11개 언어를 지원하는 드롭다운 메뉴입니다.
 * @see SPEC-192-i18n-api.md
 */
import { Languages, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { SUPPORTED_LOCALES, getLocaleInfo } from '@/config/locales';
import { useLocale } from '@/contexts/AppContext';

interface LocaleSelectorProps {
  /** 표시할 언어 티어 (기본: 전체) */
  showTiers?: (1 | 2 | 3 | 4 | 5)[];
  /** 컴팩트 모드 (아이콘만 표시) */
  compact?: boolean;
  /** 커스텀 트리거 렌더 함수 (현재 로케일 정보를 인자로 받음) */
  renderTrigger?: (currentLocale: { languageIcon: string; nativeName: string } | undefined) => React.ReactElement;
}

export function LocaleSelector({ showTiers, compact = false, renderTrigger }: LocaleSelectorProps) {
  const { locale, setLocale } = useLocale();

  // 티어 필터링
  const filteredLocales = showTiers
    ? SUPPORTED_LOCALES.filter((l) => showTiers.includes(l.tier))
    : SUPPORTED_LOCALES;

  const currentLocaleInfo = getLocaleInfo(locale);

  const triggerElement = renderTrigger
    ? renderTrigger(currentLocaleInfo)
    : (
      <Button
        variant="ghost"
        size="sm"
        className="w-auto h-10 rounded-2xl hover:bg-white/20 dark:hover:bg-black/20 px-3"
      >
        <Languages className="h-4 w-4" />
        {!compact && currentLocaleInfo && (
          <span className="ml-2 text-sm font-medium">
            {currentLocaleInfo.languageIcon} {currentLocaleInfo.nativeName}
          </span>
        )}
      </Button>
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {triggerElement}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {filteredLocales.map((localeInfo) => (
          <DropdownMenuItem
            key={localeInfo.code}
            onClick={() => setLocale(localeInfo.code)}
            className="flex items-center justify-between cursor-pointer"
          >
            <span>
              {localeInfo.languageIcon} {localeInfo.nativeName}
            </span>
            {locale === localeInfo.code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
